import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Menu, X, Phone, Mail, Shield, FileCheck, Award, ChevronDown, ArrowRight, Building2, Home as HomeIcon, MapPin, MessageCircle } from "lucide-react";
import PrivatkundenPage from "./PrivatkundenPage";
import B2BPage from "./B2BPage";
import CookieBanner from "./CookieBanner";
import ConstructionNotice from "./ConstructionNotice";
import useSeo from "./useSeo";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_warum-enthida/artifacts/w3srbodk_ETHIDA%202%20LOGO%20Homepage.png";

// Shared Header Component
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const contactRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contactRef.current && !contactRef.current.contains(event.target)) {
        setIsContactOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? "bg-[#0a0a0a]/90 backdrop-blur-xl" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-28">
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <img src={LOGO_URL} alt="ENTHIDA" className="h-24 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link 
              to="/fuer-privatkunden" 
              className={`text-sm font-medium transition-colors ${isActive("/fuer-privatkunden") ? "text-[#00d4aa]" : "text-[#a1a1aa] hover:text-white"}`}
              data-testid="nav-privatkunden"
            >
              Für Privatkunden
            </Link>
            <Link 
              to="/fuer-solarfirmen" 
              className={`text-sm font-medium transition-colors ${isActive("/fuer-solarfirmen") ? "text-[#00d4aa]" : "text-[#a1a1aa] hover:text-white"}`}
              data-testid="nav-b2b"
            >
              Für Solarfirmen
            </Link>
            
            {/* Contact Dropdown */}
            <div className="relative" ref={contactRef}>
              <button 
                onClick={() => setIsContactOpen(!isContactOpen)}
                className="bg-gradient-to-r from-[#7ed321] to-[#00d4aa] text-black font-semibold px-6 py-2.5 rounded-full transition-all duration-300 hover:scale-[1.02] shadow-[0_0_20px_rgba(0,212,170,0.3)] flex items-center gap-2"
                data-testid="header-cta"
              >
                <Phone size={16} />
                Kontakt
                <ChevronDown size={16} className={`transition-transform duration-300 ${isContactOpen ? "rotate-180" : ""}`} />
              </button>
              
              {/* Dropdown Menu */}
              {isContactOpen && (
                <div className="absolute right-0 top-full mt-3 w-80 bg-[#121212] border border-white/10 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden animate-fadeIn z-[60]">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-[#7ed321]/10 to-[#00d4aa]/10 px-6 py-4 border-b border-white/10">
                    <h3 className="text-white font-semibold text-lg">Kontaktieren Sie uns</h3>
                    <p className="text-[#a1a1aa] text-sm">Wir freuen uns auf Ihre Anfrage</p>
                  </div>
                  
                  {/* Contact Options */}
                  <div className="p-4 space-y-2">
                    {/* Phone */}
                    <a 
                      href="tel:+4917641234614"
                      className="flex items-center gap-4 p-3 rounded-xl hover:bg-white/5 transition-colors group"
                      data-testid="contact-phone"
                    >
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center group-hover:shadow-[0_0_15px_rgba(0,212,170,0.3)] transition-all">
                        <Phone size={18} className="text-[#00d4aa]" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Telefon</p>
                        <p className="text-[#00d4aa] text-sm">+49 176 41234 614</p>
                      </div>
                    </a>
                    
                    {/* Email */}
                    <a 
                      href="mailto:info@enthida.de"
                      className="flex items-center gap-4 p-3 rounded-xl hover:bg-white/5 transition-colors group"
                      data-testid="contact-email"
                    >
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center group-hover:shadow-[0_0_15px_rgba(0,212,170,0.3)] transition-all">
                        <Mail size={18} className="text-[#00d4aa]" />
                      </div>
                      <div>
                        <p className="text-white font-medium">E-Mail</p>
                        <p className="text-[#00d4aa] text-sm">info@enthida.de</p>
                      </div>
                    </a>
                    
                    {/* WhatsApp */}
                    <a 
                      href="https://wa.me/4917641234614"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 p-3 rounded-xl hover:bg-white/5 transition-colors group"
                      data-testid="contact-whatsapp"
                    >
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center group-hover:shadow-[0_0_15px_rgba(0,212,170,0.3)] transition-all">
                        <MessageCircle size={18} className="text-[#00d4aa]" />
                      </div>
                      <div>
                        <p className="text-white font-medium">WhatsApp</p>
                        <p className="text-[#00d4aa] text-sm">+49 176 41234 614</p>
                      </div>
                    </a>
                    
                    {/* Location */}
                    <div className="flex items-center gap-4 p-3 rounded-xl">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center">
                        <MapPin size={18} className="text-[#00d4aa]" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Standort</p>
                        <p className="text-[#a1a1aa] text-sm">Eggenfelden +100 km</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Footer */}
                  <div className="px-4 pb-4">
                    <div className="bg-gradient-to-r from-[#7ed321]/5 to-[#00d4aa]/5 rounded-xl p-3 border border-white/5">
                      <p className="text-[#a1a1aa] text-xs text-center">
                        Reaktionszeit: <span className="text-[#00d4aa] font-medium">24 Stunden</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      
      {/* Futuristic Green Underline */}
      <div className="relative h-[2px] w-full overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#7ed321] to-[#00d4aa]" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#00d4aa] to-transparent animate-pulse opacity-50" />
        <div className="absolute top-0 left-0 w-32 h-full bg-gradient-to-r from-[#7ed321] to-transparent blur-sm animate-[slideRight_3s_linear_infinite]" />
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden bg-[#121212] border-t border-white/10 py-4 px-6">
          <nav className="flex flex-col gap-4">
            <Link 
              to="/fuer-privatkunden" 
              className="text-[#a1a1aa] hover:text-white px-4 py-2"
              onClick={() => setIsMenuOpen(false)}
            >
              Für Privatkunden
            </Link>
            <Link 
              to="/fuer-solarfirmen" 
              className="text-[#a1a1aa] hover:text-white px-4 py-2"
              onClick={() => setIsMenuOpen(false)}
            >
              Für Solarfirmen
            </Link>
            <a 
              href="tel:+4917641234614" 
              className="mx-4 bg-gradient-to-r from-[#7ed321] to-[#00d4aa] text-black font-semibold px-6 py-2.5 rounded-full text-center"
            >
              Kontakt
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

// Shared Footer Component
const Footer = () => (
  <footer className="bg-[#0a0a0a] border-t border-white/10 py-16">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <img src={LOGO_URL} alt="ENTHIDA" className="h-16 w-auto mb-6" />
          <p className="text-[#a1a1aa] max-w-md leading-relaxed">
            Spezialisiert auf PV-Abnahmen, Inbetriebnahme und Dokumentation. 
            VDE-konforme Prüfungen mit höchsten Qualitätsstandards.
          </p>
        </div>
        <div>
          <h4 className="text-white font-medium mb-4">Leistungen</h4>
          <ul className="space-y-2 text-[#a1a1aa]">
            <li><Link to="/fuer-privatkunden" className="hover:text-[#00d4aa] transition-colors">Für Privatkunden</Link></li>
            <li><Link to="/fuer-solarfirmen" className="hover:text-[#00d4aa] transition-colors">Für Solarfirmen</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-medium mb-4">Kontakt</h4>
          <ul className="space-y-2 text-[#a1a1aa]">
            <li className="flex items-center gap-2">
              <Phone size={16} className="text-[#00d4aa]" />
              <a href="tel:+4917641234614" className="hover:text-white transition-colors">+49 176 41234 614</a>
            </li>
            <li className="flex items-center gap-2">
              <Mail size={16} className="text-[#00d4aa]" />
              <a href="mailto:info@enthida.de" className="hover:text-white transition-colors">info@enthida.de</a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-[#71717a] text-sm">
        <p>© 2026 ENTHIDA Smart Energy Systems. Alle Rechte vorbehalten.</p>
        <div className="flex gap-6">
          <Link to="/impressum" className="hover:text-[#00d4aa] transition-colors">Impressum</Link>
          <Link to="/datenschutz" className="hover:text-[#00d4aa] transition-colors">Datenschutz</Link>
          <Link to="/cookies" className="hover:text-[#00d4aa] transition-colors">Cookies</Link>
        </div>
      </div>
    </div>
  </footer>
);

// Landing Page Component
const LandingPage = () => {
  useSeo({
    title: "ENTHIDA | PV-Spezialist für Abnahme, Inbetriebnahme & Dokumentation",
    description: "Kein Standard-Elektriker. Ihr PV-Spezialist für VDE-konforme Abnahmen, Inbetriebnahme und Dokumentation – für Privatkunden und Solarfirmen.",
    path: "/",
  });
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/d607ac6b-4513-4b85-be87-20e8a661ad1d/images/b709392042161b55856c9b63d53c7e53b4c60478295f6b6f124bbd8c04b73dfc.png)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-transparent to-[#0a0a0a]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00d4aa] mb-6">Smart Energy Systems</p>
          <h1 className="text-5xl md:text-7xl tracking-tighter font-medium text-white mb-6">
            Kein Standard-Elektriker.
            <br />
            <span className="bg-gradient-to-r from-[#7ed321] to-[#00d4aa] bg-clip-text text-transparent">
              Ihr PV-Spezialist.
            </span>
          </h1>
          <p className="text-lg md:text-xl text-[#a1a1aa] max-w-2xl mx-auto mb-12 leading-relaxed">
            Spezialisiert auf PV-Abnahmen, Inbetriebnahme und Dokumentation. 
            VDE-konforme Prüfungen mit höchsten Qualitätsstandards.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/fuer-privatkunden"
              className="group bg-gradient-to-r from-[#7ed321] to-[#00d4aa] text-black font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:scale-[1.02] shadow-[0_0_20px_rgba(0,212,170,0.3)] flex items-center justify-center gap-2"
              data-testid="hero-cta-privat"
            >
              <HomeIcon size={20} />
              Für Privatkunden
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link 
              to="/fuer-solarfirmen"
              className="group bg-[#121212] border border-white/10 text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:border-[#00d4aa]/50 flex items-center justify-center gap-2"
              data-testid="hero-cta-b2b"
            >
              <Building2 size={20} />
              Für Solarfirmen
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Differentiator */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-[#121212] border border-white/10 rounded-2xl p-8 hover:border-[#00d4aa]/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center mb-6">
                <Shield className="text-[#00d4aa]" size={28} />
              </div>
              <h3 className="text-xl font-medium text-white mb-3">VDE-konform</h3>
              <p className="text-[#a1a1aa] leading-relaxed">Normgerechte Prüfungen nach aktuellen Standards für maximale Sicherheit.</p>
            </div>
            <div className="bg-[#121212] border border-white/10 rounded-2xl p-8 hover:border-[#00d4aa]/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center mb-6">
                <FileCheck className="text-[#00d4aa]" size={28} />
              </div>
              <h3 className="text-xl font-medium text-white mb-3">Alles aus einer Hand</h3>
              <p className="text-[#a1a1aa] leading-relaxed">Netzbetreiber, Marktstammdatenregister, Dokumentation – komplett abgewickelt.</p>
            </div>
            <div className="bg-[#121212] border border-white/10 rounded-2xl p-8 hover:border-[#00d4aa]/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center mb-6">
                <Award className="text-[#00d4aa]" size={28} />
              </div>
              <h3 className="text-xl font-medium text-white mb-3">Spezialisiert</h3>
              <p className="text-[#a1a1aa] leading-relaxed">Fokus auf PV-Abnahmen und Inbetriebnahme – keine Standard-Elektroarbeiten.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Impressum Page
const ImpressumPage = () => {
  useSeo({
    title: "Impressum | ENTHIDA",
    description: "Impressum und rechtliche Angaben von ENTHIDA Smart Energy Systems.",
    path: "/impressum",
  });
  return (
  <div className="min-h-screen bg-[#0a0a0a] pt-40 pb-24">
    <div className="max-w-3xl mx-auto px-6 lg:px-12">
      <h1 className="text-4xl md:text-5xl font-medium text-white mb-8">Impressum</h1>
      
      <div className="space-y-8 text-[#a1a1aa]">
        <section>
          <h2 className="text-xl font-medium text-white mb-4">Angaben gemäß § 5 TMG</h2>
          <p className="leading-relaxed">
            ENTHIDA<br />
            Smart Energy Systems<br />
            <br />
            [Straße und Hausnummer]<br />
            [PLZ] Eggenfelden<br />
            Deutschland
          </p>
        </section>

        <section>
          <h2 className="text-xl font-medium text-white mb-4">Kontakt</h2>
          <p className="leading-relaxed">
            Telefon: +49 176 41234 614<br />
            E-Mail: info@enthida.de
          </p>
        </section>

        <section>
          <h2 className="text-xl font-medium text-white mb-4">Umsatzsteuer-ID</h2>
          <p className="leading-relaxed">
            Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz:<br />
            [USt-IdNr. einfügen]
          </p>
        </section>

        <section>
          <h2 className="text-xl font-medium text-white mb-4">Berufsbezeichnung und berufsrechtliche Regelungen</h2>
          <p className="leading-relaxed">
            Berufsbezeichnung: Elektrotechniker / Elektromeister<br />
            Zuständige Kammer: [Handwerkskammer einfügen]<br />
            Verliehen in: Deutschland
          </p>
        </section>

        <section>
          <h2 className="text-xl font-medium text-white mb-4">Verbraucherstreitbeilegung / Universalschlichtungsstelle</h2>
          <p className="leading-relaxed">
            Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-medium text-white mb-4">Haftung für Inhalte</h2>
          <p className="leading-relaxed">
            Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht unter der Pflicht, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-medium text-white mb-4">Haftung für Links</h2>
          <p className="leading-relaxed">
            Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-medium text-white mb-4">Urheberrecht</h2>
          <p className="leading-relaxed">
            Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
          </p>
        </section>
      </div>
    </div>
  </div>
  );
};

// Datenschutz Page
const DatenschutzPage = () => {
  useSeo({
    title: "Datenschutz | ENTHIDA",
    description: "Datenschutzerklärung von ENTHIDA Smart Energy Systems.",
    path: "/datenschutz",
  });
  return (
  <div className="min-h-screen bg-[#0a0a0a] pt-40 pb-24">
    <div className="max-w-3xl mx-auto px-6 lg:px-12">
      <h1 className="text-4xl md:text-5xl font-medium text-white mb-8">Datenschutzerklärung</h1>
      
      <div className="space-y-8 text-[#a1a1aa]">
        <section>
          <h2 className="text-xl font-medium text-white mb-4">1. Datenschutz auf einen Blick</h2>
          <h3 className="text-lg font-medium text-white mb-2">Allgemeine Hinweise</h3>
          <p className="leading-relaxed mb-4">
            Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-medium text-white mb-4">2. Verantwortlicher</h2>
          <p className="leading-relaxed">
            ENTHIDA - Smart Energy Systems<br />
            [Straße und Hausnummer]<br />
            [PLZ] Eggenfelden<br />
            <br />
            Telefon: +49 176 41234 614<br />
            E-Mail: info@enthida.de
          </p>
        </section>

        <section>
          <h2 className="text-xl font-medium text-white mb-4">3. Datenerfassung auf dieser Website</h2>
          <h3 className="text-lg font-medium text-white mb-2">Wer ist verantwortlich für die Datenerfassung?</h3>
          <p className="leading-relaxed mb-4">
            Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten können Sie dem Abschnitt "Verantwortlicher" entnehmen.
          </p>
          <h3 className="text-lg font-medium text-white mb-2">Wie erfassen wir Ihre Daten?</h3>
          <p className="leading-relaxed mb-4">
            Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z. B. um Daten handeln, die Sie per E-Mail oder Telefon an uns übermitteln.
          </p>
          <p className="leading-relaxed">
            Andere Daten werden automatisch oder nach Ihrer Einwilligung beim Besuch der Website durch unsere IT-Systeme erfasst. Das sind vor allem technische Daten (z. B. Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs).
          </p>
        </section>

        <section>
          <h2 className="text-xl font-medium text-white mb-4">4. Ihre Rechte</h2>
          <p className="leading-relaxed mb-4">
            Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung oder Löschung dieser Daten zu verlangen.
          </p>
          <p className="leading-relaxed">
            Wenn Sie eine Einwilligung zur Datenverarbeitung erteilt haben, können Sie diese Einwilligung jederzeit für die Zukunft widerrufen. Hierzu sowie zu weiteren Fragen zum Thema Datenschutz können Sie sich jederzeit an uns wenden.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-medium text-white mb-4">5. Hosting</h2>
          <p className="leading-relaxed">
            Diese Website wird extern gehostet. Die personenbezogenen Daten, die auf dieser Website erfasst werden, werden auf den Servern des Hosters gespeichert. Hierbei kann es sich v. a. um IP-Adressen, Kontaktanfragen, Meta- und Kommunikationsdaten, Vertragsdaten, Kontaktdaten, Namen, Websitezugriffe und sonstige Daten, die über eine Website generiert werden, handeln.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-medium text-white mb-4">6. Kontaktaufnahme</h2>
          <p className="leading-relaxed">
            Wenn Sie uns per E-Mail, Telefon oder WhatsApp kontaktieren, wird Ihre Anfrage inklusive aller daraus hervorgehenden personenbezogenen Daten (Name, Anfrage) zum Zwecke der Bearbeitung Ihres Anliegens bei uns gespeichert und verarbeitet. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-medium text-white mb-4">7. Google Analytics 4</h2>
          <p className="leading-relaxed mb-4">
            Diese Website nutzt Google Analytics 4, einen Webanalysedienst der Google Ireland Limited
            ("Google"), Gordon House, Barrow Street, Dublin 4, Irland. Google Analytics verwendet
            sogenannte Cookies, die eine Analyse Ihrer Benutzung der Website ermöglichen.
          </p>
          <p className="leading-relaxed mb-4">
            Die durch das Cookie erzeugten Informationen über Ihre Benutzung dieser Website werden in
            der Regel an einen Server von Google in den USA übertragen und dort gespeichert. Wir haben
            die IP-Anonymisierung aktiviert (<code className="bg-white/5 px-1 rounded text-xs">anonymize_ip</code>),
            sodass Ihre IP-Adresse innerhalb der EU/EWR gekürzt wird.
          </p>
          <p className="leading-relaxed mb-4">
            <strong className="text-white">Cross-Domain-Tracking:</strong> Da diese Subdomain
            (partner.enthida.de) gemeinsam mit der Hauptdomain enthida.de betrieben wird, ist die
            Linker-Funktion von Google Analytics aktiviert. Dies erlaubt eine konsistente, aber weiterhin
            anonyme Auswertung Ihres Besuchs über beide Domains hinweg.
          </p>
          <p className="leading-relaxed mb-4">
            <strong className="text-white">Rechtsgrundlage:</strong> Die Verarbeitung erfolgt
            ausschließlich auf Grundlage Ihrer Einwilligung gemäß Art. 6 Abs. 1 lit. a DSGVO (Consent
            Mode v2). Bis zu Ihrer Zustimmung werden <em>keine</em> Analysedaten erhoben. Sie können
            Ihre Einwilligung jederzeit widerrufen, indem Sie in Ihrem Browser die Cookies dieser Seite
            löschen.
          </p>
          <p className="leading-relaxed">
            Weitere Informationen zum Umgang mit Nutzerdaten bei Google Analytics finden Sie in der
            Datenschutzerklärung von Google:{" "}
            <a
              href="https://policies.google.com/privacy?hl=de"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#00d4aa] hover:underline"
            >
              policies.google.com/privacy
            </a>
          </p>
        </section>

        <section>
          <h2 className="text-xl font-medium text-white mb-4">8. PostHog (Produktanalyse)</h2>
          <p className="leading-relaxed">
            Wir nutzen PostHog für anonyme Produktanalysen und aggregierte Session-Replays. Die
            Verarbeitung erfolgt nur nach Ihrer Zustimmung. Anbieter: PostHog Inc., 2261 Market Street
            #4008, San Francisco, CA 94114, USA.
          </p>
        </section>
      </div>
    </div>
  </div>
  );
};

// Cookies Page
const CookiesPage = () => {
  useSeo({
    title: "Cookie-Richtlinie | ENTHIDA",
    description: "Cookie-Richtlinie von ENTHIDA Smart Energy Systems.",
    path: "/cookies",
  });
  return (
  <div className="min-h-screen bg-[#0a0a0a] pt-40 pb-24">
    <div className="max-w-3xl mx-auto px-6 lg:px-12">
      <h1 className="text-4xl md:text-5xl font-medium text-white mb-8">Cookie-Richtlinie</h1>
      
      <div className="space-y-8 text-[#a1a1aa]">
        <section>
          <h2 className="text-xl font-medium text-white mb-4">Was sind Cookies?</h2>
          <p className="leading-relaxed">
            Cookies sind kleine Textdateien, die auf Ihrem Computer oder mobilen Gerät gespeichert werden, wenn Sie eine Website besuchen. Sie ermöglichen es der Website, sich an Ihre Aktionen und Einstellungen (wie Login, Sprache, Schriftgröße und andere Anzeigeeinstellungen) über einen bestimmten Zeitraum zu erinnern.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-medium text-white mb-4">Welche Cookies verwenden wir?</h2>
          
          <div className="bg-[#121212] border border-white/10 rounded-xl p-6 mb-4">
            <h3 className="text-lg font-medium text-white mb-2">Notwendige Cookies</h3>
            <p className="leading-relaxed text-sm">
              Diese Cookies sind für das Funktionieren der Website unbedingt erforderlich. Sie ermöglichen grundlegende Funktionen wie die Seitennavigation. Ohne diese Cookies kann die Website nicht richtig funktionieren.
            </p>
          </div>

          <div className="bg-[#121212] border border-white/10 rounded-xl p-6 mb-4">
            <h3 className="text-lg font-medium text-white mb-2">Analyse-Cookies (Google Analytics 4)</h3>
            <p className="leading-relaxed text-sm mb-3">
              Wir setzen Google Analytics 4 (Anbieter: Google Ireland Limited) ein, um die Nutzung unserer Website anonym auszuwerten und kontinuierlich zu verbessern. 
              IP-Adressen werden anonymisiert. Die Verarbeitung erfolgt nur, wenn Sie ausdrücklich zustimmen (Consent Mode v2).
            </p>
            <p className="leading-relaxed text-sm">
              <strong className="text-white">Cross-Domain-Tracking:</strong> Da diese Subdomain (partner.enthida.de) gemeinsam mit der Hauptseite (enthida.de) betrieben wird, nutzen wir die Linker-Funktion von Google Analytics. Bewegungen zwischen den Domains werden anonymisiert demselben Besuch zugeordnet, damit wir die Nutzung sauber auswerten können – ohne personenbezogene Daten zu teilen.
            </p>
          </div>

          <div className="bg-[#121212] border border-white/10 rounded-xl p-6 mb-4">
            <h3 className="text-lg font-medium text-white mb-2">Produktanalyse (PostHog)</h3>
            <p className="leading-relaxed text-sm">
              Zusätzlich verwenden wir PostHog für anonyme Produktanalysen und Session-Replays in aggregierter Form. Auch hier erfolgt die Erhebung ausschließlich mit Ihrer Zustimmung.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-medium text-white mb-4">Eingesetzte Cookies im Überblick</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-white/10 rounded-lg overflow-hidden">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left text-white font-medium p-3 border-b border-white/10">Name</th>
                  <th className="text-left text-white font-medium p-3 border-b border-white/10">Anbieter</th>
                  <th className="text-left text-white font-medium p-3 border-b border-white/10">Zweck</th>
                  <th className="text-left text-white font-medium p-3 border-b border-white/10">Speicherdauer</th>
                </tr>
              </thead>
              <tbody className="text-[#a1a1aa]">
                <tr className="border-b border-white/5">
                  <td className="p-3">_ga</td>
                  <td className="p-3">Google</td>
                  <td className="p-3">Unterscheidet Besucher</td>
                  <td className="p-3">2 Jahre</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="p-3">_ga_LPYR5SDSVE</td>
                  <td className="p-3">Google</td>
                  <td className="p-3">Sitzungszustand GA4</td>
                  <td className="p-3">2 Jahre</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="p-3">_gid</td>
                  <td className="p-3">Google</td>
                  <td className="p-3">Unterscheidet Besucher (24h)</td>
                  <td className="p-3">24 Stunden</td>
                </tr>
                <tr>
                  <td className="p-3">enthida_consent</td>
                  <td className="p-3">ENTHIDA</td>
                  <td className="p-3">Speichert Ihre Cookie-Entscheidung</td>
                  <td className="p-3">unbegrenzt (Local Storage)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-medium text-white mb-4">Wie können Sie Cookies kontrollieren?</h2>
          <p className="leading-relaxed mb-4">
            Sie können Cookies über Ihre Browsereinstellungen kontrollieren und/oder löschen. Sie können alle Cookies löschen, die sich bereits auf Ihrem Computer befinden, und Sie können die meisten Browser so einstellen, dass sie das Speichern von Cookies verhindern.
          </p>
          <p className="leading-relaxed">
            Beachten Sie jedoch, dass Sie möglicherweise einige Einstellungen manuell anpassen müssen, wenn Sie Cookies deaktivieren, und dass einige Dienste und Funktionen möglicherweise nicht funktionieren.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-medium text-white mb-4">Ihre Einwilligung</h2>
          <p className="leading-relaxed">
            Durch die weitere Nutzung unserer Website stimmen Sie der Verwendung von Cookies zu. Sie können Ihre Einwilligung jederzeit widerrufen, indem Sie die Cookies in Ihrem Browser löschen.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-medium text-white mb-4">Kontakt</h2>
          <p className="leading-relaxed">
            Bei Fragen zu unserer Cookie-Richtlinie kontaktieren Sie uns bitte unter:<br /><br />
            E-Mail: info@enthida.de<br />
            Telefon: +49 176 41234 614
          </p>
        </section>
      </div>
    </div>
  </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Header />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/fuer-privatkunden" element={<PrivatkundenPage />} />
          <Route path="/fuer-solarfirmen" element={<B2BPage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/datenschutz" element={<DatenschutzPage />} />
          <Route path="/cookies" element={<CookiesPage />} />
        </Routes>
        <Footer />
        <CookieBanner />
        <ConstructionNotice />
      </BrowserRouter>
    </div>
  );
}

export default App;
